import { create } from "zustand";

export interface SnowballProgress {
  total: number;
  completed: number;
  succeeded: number;
  failed: number;
  percent: number;
  statusMessage: string;
  currentSeed: string;
  discoveredCount: number;
}

export interface SnowballOptions {
  threshold: number;
  includeReferences: boolean;
}

interface SnowballState {
  isSnowballing: boolean;
  isCompleted: boolean;
  error: string | null;
  progress: SnowballProgress;
  options: SnowballOptions;
  abortController: AbortController | null;

  startSnowball: () => AbortController;
  /** startSnowball unless one is already running (then null) — check-and-set in one step. */
  tryStartSnowball: () => AbortController | null;
  updateProgress: (update: Partial<SnowballProgress>) => void;
  completeSnowball: (message: string) => void;
  failSnowball: (error: string) => void;
  resetSnowball: () => void;
  setOptions: (options: Partial<SnowballOptions>) => void;
  cancelSnowball: () => void;
}

const initialProgress: SnowballProgress = {
  total: 0,
  completed: 0,
  succeeded: 0,
  failed: 0,
  percent: 0,
  statusMessage: "",
  currentSeed: "",
  discoveredCount: 0,
};

const defaultOptions: SnowballOptions = {
  threshold: 80,
  includeReferences: true,
};

export const useSnowballStore = create<SnowballState>((set, get) => ({
  isSnowballing: false,
  isCompleted: false,
  error: null,
  progress: { ...initialProgress },
  options: { ...defaultOptions },
  abortController: null,

  tryStartSnowball: () => (get().isSnowballing ? null : get().startSnowball()),
  startSnowball: () => {
    const controller = new AbortController();
    set({
      isSnowballing: true,
      isCompleted: false,
      error: null,
      progress: { ...initialProgress },
      abortController: controller,
    });
    return controller;
  },

  updateProgress: (update) =>
    set((state) => ({
      progress: { ...state.progress, ...update },
    })),

  completeSnowball: (message) =>
    set({
      isSnowballing: false,
      isCompleted: true,
      progress: { ...get().progress, statusMessage: message, percent: 100 },
      abortController: null,
    }),

  failSnowball: (error) =>
    set({
      isSnowballing: false,
      error,
      abortController: null,
    }),

  resetSnowball: () =>
    set({
      isSnowballing: false,
      isCompleted: false,
      error: null,
      progress: { ...initialProgress },
      abortController: null,
    }),

  setOptions: (options) =>
    set((state) => ({
      options: { ...state.options, ...options },
    })),

  cancelSnowball: () => {
    const { abortController } = get();
    if (abortController) {
      abortController.abort();
    }
    set({
      isSnowballing: false,
      error: "Cancelled by user",
      abortController: null,
    });
  },
}));
